import './ProductsSection.css';

import { motion } from 'framer-motion';
import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { products } from '../../data/products';
import OptimizedImage from '../OptimizedImage';

const ProductsSection = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const navigate = useNavigate();

  const handleCardNavigation = (slug: string) => {
    navigate(`/products/${slug}`);
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>, slug: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardNavigation(slug);
    }
  };

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'books', name: 'Books' },
    { id: 'playbooks', name: 'Playbooks' },
    { id: 'exercises', name: 'Exercises' },
    { id: 'games', name: 'Card Games' },
  ];

  const filteredProducts = activeCategory === 'all'
    ? products
    : products.filter(product => product.category === activeCategory);

  return (
    <section id="products" className="section bg-surface dark:bg-panel/90">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-primary dark:text-theme-primary mb-4">
            growth tools
          </h2>
          <div className="w-24 h-1 bg-secondary mx-auto mb-6"></div>
          <p className="text-lg text-theme-secondary max-w-3xl mx-auto">
            Explore our collection of research-based solutions designed to help you learn, evolve, and konnect better.
          </p>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-4 py-2 rounded-full transition-colors border ${
                activeCategory === category.id
                  ? 'bg-brand-primary text-white dark:bg-brand-secondary dark:text-brand-primary border-transparent shadow-surface'
                  : 'bg-card dark:bg-panel text-theme-secondary border-theme hover:bg-card/80 dark:hover:bg-panel/80'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredProducts.map((product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-card dark:bg-panel/80 rounded-lg overflow-hidden shadow-surface/60 hover:shadow-surface transition-all cursor-pointer h-full flex flex-col"
              role="button"
              tabIndex={0}
              aria-label={`View details for ${product.title}`}
              onClick={() => handleCardNavigation(product.slug)}
              onKeyDown={(event) => handleCardKeyDown(event, product.slug)}
            >
              <div className="aspect-[4/3] bg-card dark:bg-panel relative overflow-hidden">
                <OptimizedImage
                  src={product.image}
                  alt={product.title}
                  className="transition-transform hover:scale-105"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-xl font-semibold text-primary dark:text-theme-primary mb-2">
                  {product.title}
                </h3>
                <p className="text-theme-secondary mb-6">
                  {product.description}
                </p>
                <Link
                  to={`/products/${product.slug}`}
                  onClick={(event) => event.stopPropagation()}
                  className="inline-flex items-center text-secondary font-medium hover:underline mt-auto"
                >
                  Read more
                </Link>
              </div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default ProductsSection;
